// Rudras-AI Commerce Engine — printer service (Mar 2026).
//
// Web-Bluetooth-only first driver. The MUNBYN thermal printer (and most
// 58/80mm ESC/POS receipt printers) expose a GATT Serial Port Profile-
// like service. We connect, write the ESC/POS bytes in 200-byte chunks
// (the BLE MTU ceiling), and disconnect.
//
// Driver registry contract: every driver implements `connect()`,
// `printBytes(bytes)`, `disconnect()`, and exposes `name`. Future
// drivers (network IP, USB, cloud) plug in without touching the
// caller. Keeps printer choice purely a UX/config concern.

// ESC/POS-compatible Bluetooth printers usually advertise one of these
// service UUIDs. We probe all three with `acceptAllDevices=false`
// disabled so the browser's pairing dialog is constrained to printers.
const PRINTER_SERVICE_CANDIDATES = [
  "000018f0-0000-1000-8000-00805f9b34fb", // generic ESC/POS BT class (MUNBYN, Goojprt)
  "0000ffe0-0000-1000-8000-00805f9b34fb", // HM-10 / common BLE serial
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // ISSC / Microchip BLE
];

const WRITE_CHARACTERISTIC_CANDIDATES = [
  "00002af1-0000-1000-8000-00805f9b34fb",
  "0000ffe1-0000-1000-8000-00805f9b34fb",
  "49535343-8841-43f4-a8d4-ecbe34729bb3",
];

const CHUNK = 200; // BLE MTU-safe chunk size.

// In-memory cache so we can reuse a connection across two consecutive
// "Print Receipt" taps without re-prompting the customer.
let _cachedDevice = null;
let _cachedCharacteristic = null;

function isSupported() {
  return typeof navigator !== "undefined" && !!navigator.bluetooth && !!navigator.bluetooth.requestDevice;
}

async function connect() {
  if (!isSupported()) {
    const err = new Error("Web Bluetooth is not supported in this browser. Use Chrome on Android or desktop.");
    err.code = "unsupported";
    throw err;
  }
  if (_cachedCharacteristic && _cachedDevice && _cachedDevice.gatt && _cachedDevice.gatt.connected) {
    return _cachedCharacteristic;
  }
  const device = await navigator.bluetooth.requestDevice({
    filters: PRINTER_SERVICE_CANDIDATES.map((u) => ({ services: [u] })),
    optionalServices: PRINTER_SERVICE_CANDIDATES,
  });
  const server = await device.gatt.connect();
  let chosen = null;
  for (const svcId of PRINTER_SERVICE_CANDIDATES) {
    try {
      const svc = await server.getPrimaryService(svcId);
      for (const charId of WRITE_CHARACTERISTIC_CANDIDATES) {
        try {
          const c = await svc.getCharacteristic(charId);
          chosen = c; break;
        } catch (_e) { /* try next */ }
      }
      if (chosen) break;
    } catch (_e) { /* try next service */ }
  }
  if (!chosen) {
    try { device.gatt.disconnect(); } catch (_e) { /* ignore */ }
    const err = new Error("Could not find an ESC/POS write characteristic on the selected printer.");
    err.code = "no_characteristic";
    throw err;
  }
  device.addEventListener("gattserverdisconnected", () => {
    _cachedCharacteristic = null;
  });
  _cachedDevice = device;
  _cachedCharacteristic = chosen;
  return chosen;
}

async function printBytes(bytes) {
  const ch = await connect();
  const buf = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < buf.length; i += CHUNK) {
    const slice = buf.subarray(i, Math.min(i + CHUNK, buf.length));
    // `writeValueWithoutResponse` is faster but not all stacks support it;
    // fall back to `writeValue` (default ack-required path).
    if (ch.writeValueWithoutResponse) {
      // eslint-disable-next-line no-await-in-loop
      await ch.writeValueWithoutResponse(slice);
    } else {
      // eslint-disable-next-line no-await-in-loop
      await ch.writeValue(slice);
    }
  }
}

function disconnect() {
  try {
    if (_cachedDevice && _cachedDevice.gatt && _cachedDevice.gatt.connected) {
      _cachedDevice.gatt.disconnect();
    }
  } catch (_e) { /* ignore */ }
  _cachedDevice = null;
  _cachedCharacteristic = null;
}

// Bluetooth driver — registered as the V1 default. Future network /
// USB / cloud drivers add themselves to this map.
const DRIVERS = {
  bluetooth: { name: "Bluetooth", isSupported, connect, printBytes, disconnect },
};

export default {
  drivers: DRIVERS,
  defaultDriver: () => DRIVERS.bluetooth,
  isSupported: () => Object.values(DRIVERS).some((d) => d.isSupported()),
  // Convenience: pick the default driver and dispatch.
  printBytes: async (bytes) => DRIVERS.bluetooth.printBytes(bytes),
  disconnect: () => DRIVERS.bluetooth.disconnect(),
};
